/**
 * HERO — trust / compliance row (prototype "VERTRAUEN / COMPLIANCE", desktop
 * lines 2043–2074). Sits between the product window and the scroll stage:
 * DSGVO-konform · GoBD · Serverstandort EU · Entwickelt in Deutschland.
 */
import { FileCheck, ShieldCheck } from "lucide-react";
import { useTranslations } from "next-intl";
import { Fragment, type ReactNode } from "react";

import { H } from "./hero-tokens";

/** 12 EU stars at clock positions (verbatim from the prototype SVG). */
const EU_STARS: readonly (readonly [number, number])[] = [
  [11, 3.8],
  [14.6, 4.77],
  [17.23, 7.4],
  [18.2, 11],
  [17.23, 14.6],
  [14.6, 17.23],
  [11, 18.2],
  [7.4, 17.23],
  [3.77, 14.6],
  [3.8, 11],
  [3.77, 7.4],
  [7.4, 4.77],
];

function EuFlag(): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  return (
    <span
      style={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        display: "inline-flex",
        flexShrink: 0,
        boxShadow: "0 0 0 1px rgba(11,15,25,0.08)",
      }}
      role="img"
      aria-label={t("euFlaggeServerstandortEu")}
      title={t("serverstandortEu")}
    >
      <svg width="20" height="20" viewBox="0 0 22 22" aria-hidden="true">
        <circle cx="11" cy="11" r="11" fill="#003399" />
        {EU_STARS.map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.15" fill="#FFCC00" />
        ))}
      </svg>
    </span>
  );
}

function DeFlag(): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  return (
    <span
      style={{
        width: 20,
        height: 20,
        borderRadius: 5,
        overflow: "hidden",
        display: "inline-flex",
        flexDirection: "column",
        flexShrink: 0,
        boxShadow: "0 0 0 1px rgba(11,15,25,0.08)",
      }}
      role="img"
      aria-label={t("deutschlandFlaggeEntwickeltDeutschland")}
      title={t("entwickeltDeutschland")}
    >
      <span style={{ flex: 1, background: "#0B0F19" }} />
      <span style={{ flex: 1, background: "#DD0000" }} />
      <span style={{ flex: 1, background: "#FFCE00" }} />
    </span>
  );
}

type ComplianceItem = { readonly icon: ReactNode; readonly labelKey: string };

const ITEMS: readonly ComplianceItem[] = [
  {
    icon: <ShieldCheck size={19} strokeWidth={1.8} />,
    labelKey: "dsgvoKonform",
  },
  {
    icon: <FileCheck size={19} strokeWidth={1.8} />,
    labelKey: "gobdKonformesArbeitenMoeglich",
  },
  { icon: <EuFlag />, labelKey: "serverstandortEu" },
  { icon: <DeFlag />, labelKey: "entwickeltDeutschland" },
];

export function HeroCompliance({
  compact = false,
}: {
  readonly compact?: boolean;
}): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  return (
    <div
      style={{
        margin: compact ? "30px auto 0" : "28px auto 0",
        padding: "0 20px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 2,
          maxWidth: 1180,
        }}
      >
        {ITEMS.map((item, idx) => (
          <Fragment key={item.labelKey}>
            {idx > 0 && !compact ? (
              <span
                style={{
                  width: 1,
                  height: 22,
                  background: "rgba(11,15,25,0.1)",
                  flexShrink: 0,
                }}
              />
            ) : null}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                padding: compact ? "7px 13px" : "7px 22px",
              }}
            >
              <span
                style={{
                  color: "rgba(11,15,25,0.5)",
                  display: "inline-flex",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </span>
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: "rgba(11,15,25,0.68)",
                  whiteSpace: "nowrap",
                  fontFamily: H.sans,
                }}
              >
                {t(item.labelKey)}
              </span>
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
